import React, { useState } from 'react';
import { Save, Bell, MessageSquare, Shield, Database, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { User } from '../../types';
import { useSecureSettings } from '../../hooks/useSecureSettings';
import { useReminderRules } from '../../hooks/useReminderRules';

interface SettingsProps {
  user: User;
  initialTab?: 'reminders' | 'integrations' | 'security';
}

export const Settings: React.FC<SettingsProps> = ({ user, initialTab = 'reminders' }) => {
  const [activeTab, setActiveTab] = useState<'reminders' | 'integrations' | 'security'>(initialTab);
  const { settings: apiKeys, loading: settingsLoading, updateSettings, clearSettings } = useSecureSettings();
  const { settings, setSettings, loading: rulesLoading, saving: rulesSaving, saveRules } = useReminderRules();
  const [saveSuccess, setSaveSuccess] = useState<boolean | null>(null);

  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [testSmsData, setTestSmsData] = useState({
    phoneNumber: '',
    message: 'Hello! This is a test message from AttendAI to verify SMS functionality is working correctly.'
  });
  const [testEmailData, setTestEmailData] = useState({
    email: '',
    template: 'high-risk-72h'
  });

  const tabs = [
    { id: 'reminders', label: 'Reminders', icon: Bell },
    ...((user.role === 'admin' || user.role === 'super-admin' || user.role === 'integration-user') ? [{ id: 'integrations', label: 'Integrations', icon: Database }] : []),
    ...((user.role === 'admin' || user.role === 'super-admin') ? [{ id: 'security', label: 'Security', icon: Shield }] : [])
  ];

  const timingOptions = ['1h', '2h', '4h', '12h', '24h', '48h', '72h'];
  const channelOptions = ['email', 'sms', 'voice'];

  const saveSettings = async () => {
    const ok = await saveRules(settings);
    setSaveSuccess(ok);
    setTimeout(() => setSaveSuccess(null), 3000);
  };

  const addReminder = (riskLevel: keyof ReminderSettings) => {
    setSettings(prev => ({
      ...prev,
      [riskLevel]: {
        ...prev[riskLevel],
        reminders: [...prev[riskLevel].reminders, { timing: '24h', channels: ['email'] }]
      }
    }));
  };

  const removeReminder = (riskLevel: keyof ReminderSettings, index: number) => {
    setSettings(prev => ({
      ...prev,
      [riskLevel]: {
        ...prev[riskLevel],
        reminders: prev[riskLevel].reminders.filter((_, i) => i !== index)
      }
    }));
  };

  const updateReminder = (riskLevel: keyof ReminderSettings, index: number, field: 'timing' | 'channels', value: any) => {
    setSettings(prev => ({
      ...prev,
      [riskLevel]: {
        ...prev[riskLevel],
        reminders: prev[riskLevel].reminders.map((reminder, i) => 
          i === index ? { ...reminder, [field]: value } : reminder
        )
      }
    }));
  };

  const toggleChannel = (riskLevel: keyof ReminderSettings, reminderIndex: number, channel: string) => {
    const currentChannels = settings[riskLevel].reminders[reminderIndex].channels;
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter(c => c !== channel)
      : [...currentChannels, channel];
    
    updateReminder(riskLevel, reminderIndex, 'channels', newChannels);
  };

  const testConnection = async (service: 'twilio' | 'sendgrid' | 'hospital') => {
    setTestingConnection(service);
    
    try {
      let isValid = false;
      
      switch (service) {
        case 'twilio':
          // Test Twilio connection
          if (apiKeys.twilio.sid && apiKeys.twilio.token) {
            try {
              // Use a more reliable test by checking account details
              const accountUrl = `https://api.twilio.com/2010-04-01/Accounts/${apiKeys.twilio.sid}.json`;
              const authHeader = btoa(`${apiKeys.twilio.sid}:${apiKeys.twilio.token}`);
              
              const response = await fetch(accountUrl, {
                method: 'GET',
                headers: {
                  'Authorization': `Basic ${authHeader}`,
                  'Content-Type': 'application/json'
                },
                mode: 'cors'
              });
              
              if (response.ok) {
                const accountData = await response.json();
                isValid = accountData.sid === apiKeys.twilio.sid;
              } else {
                console.error('Twilio API response:', response.status, response.statusText);
                isValid = false;
              }
            } catch (error) {
              console.error('Twilio connection test error:', error);
              isValid = false;
            }
          }
          break;
          
        case 'sendgrid':
          // Test SendGrid connection
          if (apiKeys.sendgrid.key) {
            try {
              const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email-reminder`;
              const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  action: 'test-connection',
                  sendgridApiKey: apiKeys.sendgrid.key,
                }),
              });

              const result = await response.json();
              isValid = response.ok && result.valid === true;
              console.log(isValid
                ? 'SendGrid connection successful'
                : 'SendGrid connection failed. Please check the API key and permissions.');
            } catch (error) {
              console.log('SendGrid connection test error:', error);
              isValid = false;
            }
          }
          break;
          
        case 'hospital':
          // Test Hospital connection
          if (apiKeys.hospital.endpoint && apiKeys.hospital.key) {
            try {
              const response = await fetch(`${apiKeys.hospital.endpoint}/health`, {
                headers: {
                  'Authorization': `Bearer ${apiKeys.hospital.key}`,
                  'Content-Type': 'application/json'
                }
              });
              isValid = response.ok;
            } catch (error) {
              isValid = false;
            }
          }
          break;
      }
      
      // Update verification status
      if (isValid !== apiKeys[service].verified) {
        const updatedSettings = {
          ...apiKeys,
          [service]: { ...apiKeys[service], verified: isValid }
        };
        
        await updateSettings(updatedSettings);
        
        // Show success/error message
        if (isValid) {
          console.log(`✅ ${service.charAt(0).toUpperCase() + service.slice(1)} connection verified successfully!`);
        } else {
          console.log(`❌ ${service.charAt(0).toUpperCase() + service.slice(1)} connection failed. Please check your credentials.`);
        }
      }
      
    } catch (error) {
      console.log(`❌ Error testing ${service} connection:`, error);
      // Only update if status actually changed
      if (apiKeys[service].verified !== false) {
        const updatedSettings = {
          ...apiKeys,
          [service]: { ...apiKeys[service], verified: false }
        };
        
        await updateSettings(updatedSettings);
      }
    } finally {
      setTestingConnection(null);
    }
  };

  const sendTestSms = async () => {
    if (!apiKeys.twilio.verified || !testSmsData.phoneNumber || !testSmsData.message || !apiKeys.twilio.phoneNumber) {
      alert('Please ensure Twilio is connected, phone number is configured, and all fields are filled.');
      return;
    }

    setTestingConnection('sms');
    
    try {
      // Call Supabase Edge Function to send SMS
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-test-sms`;
      const headers = {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          to: testSmsData.phoneNumber,
          message: testSmsData.message,
          twilioSid: apiKeys.twilio.sid,
          twilioToken: apiKeys.twilio.token,
          twilioPhoneNumber: apiKeys.twilio.phoneNumber
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send SMS');
      }

      const result = await response.json();
      alert(`✅ SMS sent successfully! Message SID: ${result.messageSid}`);
      
      // Clear the form
      setTestSmsData(prev => ({ ...prev, phoneNumber: '', message: 'Hello! This is a test message from AttendAI to verify SMS functionality is working correctly.' }));
      
    } catch (error: any) {
      console.error('SMS test error:', error);
      alert(`❌ Failed to send SMS: ${error.message}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const sendTestEmail = async () => {
    if (!apiKeys.sendgrid.verified || !testEmailData.email) {
      alert('Please ensure SendGrid is connected and email address is provided.');
      return;
    }

    setTestingConnection('email');
    
    try {
      // Prepare test email content based on template
      const templateVariables = {
        patientName: 'John Doe',
        appointmentDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString(),
        appointmentTime: '10:00 AM',
        department: 'Cardiology',
        location: 'Main Hospital',
        visitType: 'Follow-up',
        hospitalName: 'Demo Hospital',
        hospitalPhone: '+1 (555) 123-4567',
        hospitalAddress: '123 Healthcare Ave, Medical City, MC 12345',
        confirmUrl: 'https://attendai.io/confirm/demo'
      };

      // Call the email edge function directly for testing
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email-reminder`;
      const headers = {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          templateType: testEmailData.template,
          templateVariables,
          to: testEmailData.email,
          sendgridApiKey: apiKeys.sendgrid.key,
          fromEmail: 'noreply@attendai.io',
          fromName: 'AttendAI Demo'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send email');
      }

      const result = await response.json();
      alert(`✅ Test email sent successfully! Message ID: ${result.messageId}`);
      
      // Clear the form
      setTestEmailData(prev => ({ ...prev, email: '' }));
      
    } catch (error: any) {
      console.error('Email test error:', error);
      alert(`❌ Failed to send email: ${error.message}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'highRisk': return 'border-red-200 bg-red-50';
      case 'mediumRisk': return 'border-yellow-200 bg-yellow-50';
      case 'lowRisk': return 'border-green-200 bg-green-50';
      default: return 'border-gray-200 bg-gray-50';
    }
  };

  const getRiskLabel = (riskLevel: string) => {
    switch (riskLevel) {
      case 'highRisk': return 'High Risk';
      case 'mediumRisk': return 'Medium Risk';
      case 'lowRisk': return 'Low Risk';
      default: return '';
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-2">Configure system preferences and integrations</p>
      </div>

      <div className="bg-white rounded-lg border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'reminders' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Reminder Rules</h3>
                <p className="text-gray-600 mb-6">Configure automated reminders based on no-show risk levels</p>
              </div>

              {Object.entries(settings).map(([riskLevel, config]) => (
                <div key={riskLevel} className={`border rounded-lg p-6 ${getRiskColor(riskLevel)}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">{getRiskLabel(riskLevel)}</h4>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          [riskLevel]: { ...prev[riskLevel as keyof ReminderSettings], enabled: e.target.checked }
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Enabled</span>
                    </label>
                  </div>

                  {config.enabled && (
                    <div className="space-y-4">
                      {config.reminders.map((reminder, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Send reminder
                              </label>
                              <select
                                value={reminder.timing}
                                onChange={(e) => updateReminder(riskLevel as keyof ReminderSettings, index, 'timing', e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {timingOptions.map(time => (
                                  <option key={time} value={time}>{time} before</option>
                                ))}
                              </select>
                            </div>

                            <div className="flex-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Channels
                              </label>
                              <div className="flex space-x-2">
                                {channelOptions.map(channel => (
                                  <button
                                    key={channel}
                                    type="button"
                                    onClick={() => toggleChannel(riskLevel as keyof ReminderSettings, index, channel)}
                                    className={`px-3 py-2 text-xs rounded-md border transition-colors ${
                                      reminder.channels.includes(channel)
                                        ? 'bg-blue-100 border-blue-300 text-blue-700'
                                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {channel}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <button
                              onClick={() => removeReminder(riskLevel as keyof ReminderSettings, index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              disabled={config.reminders.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        onClick={() => addReminder(riskLevel as keyof ReminderSettings)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        + Add reminder
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'integrations' && (
            (user.role === 'admin' || user.role === 'super-admin' || user.role === 'integration-user') ? (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">API Integrations</h3>
                <p className="text-gray-600 mb-6">Configure external service connections</p>
              </div>

              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="w-6 h-6 text-blue-600" />
                      <div>
                        <h4 className="font-medium text-gray-900">Twilio (SMS/Voice)</h4>
                        <p className="text-sm text-gray-600">Send SMS and voice call reminders</p>
                      </div>
                    </div>
                    <div className={`px-2 py-1 text-xs rounded-full ${
                      apiKeys.twilio.verified 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {apiKeys.twilio.verified ? 'Connected' : 'Not Connected'}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account SID
                      </label>
                      <input
                        type="password"
                        value={apiKeys.twilio.sid}
                        onChange={(e) => updateSettings({
                          ...apiKeys,
                          twilio: { ...apiKeys.twilio, sid: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter Twilio Account SID"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Auth Token
                      </label>
                      <input
                        type="password"
                        value={apiKeys.twilio.token}
                        onChange={(e) => updateSettings({
                          ...apiKeys,
                          twilio: { ...apiKeys.twilio, token: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter Twilio Auth Token"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Twilio Phone Number
                      </label>
                      <input
                        type="tel"
                        value={apiKeys.twilio.phoneNumber}
                        onChange={(e) => updateSettings({
                          ...apiKeys,
                          twilio: { ...apiKeys.twilio, phoneNumber: e.target.value, verified: false }
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+1234567890 (Your Twilio phone number)"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This is the phone number from your Twilio console that will send SMS messages
                      </p>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => testConnection('twilio')}
                    disabled={!apiKeys.twilio.sid || !apiKeys.twilio.token || !apiKeys.twilio.phoneNumber || testingConnection === 'twilio'}
                    className={`mt-4 px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      apiKeys.twilio.verified 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {testingConnection === 'twilio' 
                      ? 'Testing...' 
                      : apiKeys.twilio.verified 
                        ? '✅ Connection Verified' 
                        : 'Test Connection'
                    }
                  </button>
                  
                  {apiKeys.twilio.verified && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h5 className="font-medium text-blue-900 mb-3">Test SMS Functionality</h5>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Test Phone Number
                          </label>
                          <input
                            type="tel"
                            value={testSmsData.phoneNumber}
                            onChange={(e) => setTestSmsData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="+1234567890"
                          />
                          <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +1 for US)</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Test Message
                          </label>
                          <textarea
                            value={testSmsData.message}
                            onChange={(e) => setTestSmsData(prev => ({ ...prev, message: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                            rows={3}
                            placeholder="Hello! This is a test message from AttendAI."
                          />
                        </div>
                        
                        <button
                          onClick={sendTestSms}
                          disabled={!testSmsData.phoneNumber || !testSmsData.message || !apiKeys.twilio.phoneNumber || testingConnection === 'sms'}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {testingConnection === 'sms' ? 'Sending...' : 'Send Test SMS'}
                        </button>
                        
                        {!apiKeys.twilio.phoneNumber && (
                          <p className="text-sm text-red-600 mt-2">
                            Please enter your Twilio phone number above to send SMS
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="w-6 h-6 text-green-600" />
                      <div>
                        <h4 className="font-medium text-gray-900">SendGrid (Email)</h4>
                        <p className="text-sm text-gray-600">Send email reminders and notifications</p>
                      </div>
                    </div>
                    <div className={`px-2 py-1 text-xs rounded-full ${
                      apiKeys.sendgrid.verified 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {apiKeys.sendgrid.verified ? 'Connected' : 'Not Connected'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key
                    </label>
                    <input
                      type="password"
                      value={apiKeys.sendgrid.key}
                      onChange={(e) => updateSettings({
                        ...apiKeys,
                        sendgrid: { ...apiKeys.sendgrid, key: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter SendGrid API Key"
                    />
                  </div>
                  
                  <button 
                    onClick={() => testConnection('sendgrid')}
                    disabled={!apiKeys.sendgrid.key || testingConnection === 'sendgrid'}
                    className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {testingConnection === 'sendgrid' 
                      ? 'Testing...' 
                      : apiKeys.sendgrid.verified 
                        ? '✅ Connection Verified' 
                        : 'Test Connection'
                    }
                  </button>
                  
                  {apiKeys.sendgrid.verified && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h5 className="font-medium text-green-900 mb-3">Test Email Functionality</h5>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Test Email Address
                          </label>
                          <input
                            type="email"
                            value={testEmailData.email}
                            onChange={(e) => setTestEmailData(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="test@example.com"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Template
                          </label>
                          <select
                            value={testEmailData.template}
                            onChange={(e) => setTestEmailData(prev => ({ ...prev, template: e.target.value }))}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="high-risk-72h">High Risk - 72h Reminder</option>
                            <option value="medium-risk-48h">Medium Risk - 48h Reminder</option>
                            <option value="low-risk-24h">Low Risk - 24h Reminder</option>
                          </select>
                        </div>
                        
                        <button
                          onClick={sendTestEmail}
                          disabled={!testEmailData.email || testingConnection === 'email'}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {testingConnection === 'email' ? 'Sending...' : 'Send Test Email'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <Database className="w-6 h-6 text-purple-600" />
                      <div>
                        <h4 className="font-medium text-gray-900">Hospital System API</h4>
                        <p className="text-sm text-gray-600">Connect to your existing appointment system</p>
                      </div>
                    </div>
                    <div className={`px-2 py-1 text-xs rounded-full ${
                      apiKeys.hospital.verified 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {apiKeys.hospital.verified ? 'Connected' : 'Pending Setup'}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Endpoint
                      </label>
                      <input
                        type="text"
                        value={apiKeys.hospital.endpoint}
                        onChange={(e) => updateSettings({
                          ...apiKeys,
                          hospital: { ...apiKeys.hospital, endpoint: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://your-hospital-api.com/v1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Key
                      </label>
                      <input
                        type="password"
                        value={apiKeys.hospital.key}
                        onChange={(e) => updateSettings({
                          ...apiKeys,
                          hospital: { ...apiKeys.hospital, key: e.target.value }
                        })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter Hospital API Key"
                      />
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => testConnection('hospital')}
                    disabled={!apiKeys.hospital.endpoint || !apiKeys.hospital.key || testingConnection === 'hospital'}
                    className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {testingConnection === 'hospital' ? 'Testing...' : 'Test Connection'}
                  </button>
                </div>

                {(user.role === 'admin' || user.role === 'super-admin') && (
                  <div className="border border-red-200 rounded-lg p-6 bg-red-50">
                    <h4 className="font-medium text-red-900 mb-4">Danger Zone</h4>
                    <p className="text-sm text-red-700 mb-4">
                      Clear all stored API credentials. This action cannot be undone.
                    </p>
                    <button
                      onClick={async () => {
                        if (confirm('Are you sure you want to clear all API credentials? This cannot be undone.')) {
                          const success = await clearSettings();
                          if (success) {
                            alert('✅ All API credentials cleared successfully');
                          } else {
                            alert('❌ Failed to clear credentials');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                      Clear All Credentials
                    </button>
                  </div>
                )}
              </div>
            </div>
            ) : (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Admin Access Required</h3>
                <p className="text-gray-600">Only administrators can configure system integrations.</p>
              </div>
            )
          )}

          {activeTab === 'security' && (
            (user.role === 'admin' || user.role === 'super-admin') ? (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Security & Compliance</h3>
                <p className="text-gray-600 mb-6">Manage data protection and compliance settings</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Data Privacy</h4>
                    
                    <div className="space-y-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={true}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          readOnly
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Auto-anonymize patient data after 30 days
                        </span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={true}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          readOnly
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Encrypt all data transmissions (TLS 1.3)
                        </span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={false}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Enable audit logging for all data access
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">GDPR Compliance</h4>
                    
                    <div className="space-y-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={true}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          readOnly
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Require explicit consent for data processing
                        </span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={true}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          readOnly
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Allow patients to request data deletion
                        </span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={true}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          readOnly
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Maintain data processing records
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Access Control</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Session Timeout (minutes)
                        </label>
                        <input
                          type="number"
                          value={30}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={true}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Require two-factor authentication
                        </span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={false}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          Log all user activities
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Backup & Recovery</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Backup Frequency
                        </label>
                        <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Retention Period (days)
                        </label>
                        <input
                          type="number"
                          value={90}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            ) : (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Admin Access Required</h3>
                <p className="text-gray-600">Only administrators can configure security settings.</p>
              </div>
            )
          )}

          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            {saveSuccess === true && (
              <span className="flex items-center space-x-1 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>Settings saved successfully</span>
              </span>
            )}
            {saveSuccess === false && (
              <span className="flex items-center space-x-1 text-sm text-red-600">
                <XCircle className="w-4 h-4" />
                <span>Failed to save settings</span>
              </span>
            )}
            <button
              onClick={saveSettings}
              disabled={rulesSaving}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{rulesSaving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};